import React, { useEffect, useState } from "react";
import "./Statistic.scss";
import DynamicHeader from "../../Header/DynamicHeader";
import SubscriptionUpsellModal from "../Subscription/SubscriptionUpsellModal";
import { useSubscription } from "../../../contexts/SubscriptionContext";
import { getAccessToken } from "../../../awsAuth";
import comingSoon from "../img/cvantik-coming-soon.png";
import { photoVariant } from "../../../utils/imageUrl";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const STATUS_LABEL = {
  active: "Активне",
  sold: "Продано",
  archived: "В архіві",
};

function StatRow({ label, value }) {
  return (
    <div className="statistic-summary__item">
      <div className="statistic-summary__value">{value}</div>
      <div className="statistic-summary__label">{label}</div>
    </div>
  );
}

function ListingRow({ item }) {
  return (
    <div className="statistic-item">
      {item.mainPhoto ? (
        <img className="statistic-item__photo" src={photoVariant(item.mainPhoto, "xs")} alt="" loading="lazy" decoding="async" />
      ) : (
        <div className="statistic-item__photo statistic-item__photo--empty" />
      )}

      <div className="statistic-item__body">
        <div className="statistic-item__title">{item.title}</div>
        {item.status && item.status !== "active" && (
          <div className="statistic-item__status">
            {STATUS_LABEL[item.status] || item.status}
          </div>
        )}

        <div className="statistic-item__metrics">
          <span title="Перегляди">👁 {item.views}</span>
          <span title="Збереження">🔖 {item.saves}</span>
          <span title="Вподобання">❤ {item.likes}</span>
          <span title="Коментарі">💬 {item.comments}</span>
        </div>
      </div>
    </div>
  );
}

const Statistic = () => {
  const subscription = useSubscription();
  const canView = !!subscription?.entitlements?.basicStats;

  const [upsellOpen, setUpsellOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (subscription.loading || !canView) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const token = await getAccessToken?.().catch(() => null);

        if (!token) {
          if (!cancelled) setError("Увійдіть у акаунт, щоб переглянути статистику.");
          return;
        }

        const res = await fetch(`${API_BASE}/api/analytics/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(json?.error || "Не вдалося завантажити статистику");
        }

        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Не вдалося завантажити статистику");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [canView, subscription.loading]);

  return (
    <>
      <DynamicHeader
        title="Статистика"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div className="statistic">
        {!canView && !subscription.loading && (
          <div className="statistic-card">
            <div className="coming-soon">
              <img
                className="coming-soon__img"
                src={comingSoon}
                alt="Cvanto — statistics"
              />
              <div className="coming-soon__text">
                <div className="title">Статистика — на Cvanto Plus</div>
                <div className="subtitle">
                  Перегляди, збереження, вподобання й коментарі ваших оголошень
                </div>
                <button
                  type="button"
                  className="statistic-upgrade-btn"
                  onClick={() => setUpsellOpen(true)}
                >
                  Спробувати Cvanto Plus
                </button>
              </div>
            </div>
          </div>
        )}

        {canView && loading && (
          <div className="statistic-card">
            <p className="statistic-loading">Завантажуємо статистику…</p>
          </div>
        )}

        {canView && !loading && error && (
          <div className="statistic-card">
            <p className="statistic-error">{error}</p>
          </div>
        )}

        {canView && !loading && !error && data && (
          <>
            <div className="statistic-card statistic-summary">
              <StatRow label="Оголошення" value={data.totals.listings} />
              <StatRow label="Перегляди" value={data.totals.views} />
              <StatRow label="Збереження" value={data.totals.saves} />
              <StatRow label="Вподобання" value={data.totals.likes} />
              <StatRow label="Коментарі" value={data.totals.comments} />
            </div>

            <div className="statistic-section-title">Ваші оголошення</div>

            {data.items.length === 0 ? (
              <div className="statistic-card">
                <p className="statistic-empty">У вас ще немає оголошень.</p>
              </div>
            ) : (
              <div className="statistic-card statistic-list">
                {data.items.map((item) => (
                  <ListingRow key={item.listingId} item={item} />
                ))}
              </div>
            )}

            {data.topPerforming?.length > 0 && (
              <>
                <div className="statistic-section-title">Найкращі оголошення</div>
                <div className="statistic-card statistic-list">
                  {data.topPerforming.map((item) => (
                    <ListingRow key={item.listingId} item={item} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <SubscriptionUpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        reason="analytics"
      />
    </>
  );
};

export default Statistic;
